import imageCompression from 'browser-image-compression';

// Construction photos need more detail than greenmap (2560px vs 1920px, 1.5MB vs 600KB)
const TARGET_COMPRESSED_SIZE_BYTES = 1.5 * 1024 * 1024; // 1.5MB

export interface CompressedImageResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  /** True if the file was meaningfully reduced (>5% smaller). Used to decide whether to show feedback. */
  wasCompressed: boolean;
}

/**
 * Compress an image file before upload.
 *
 * Always strips EXIF data (privacy — GPS coords removed via canvas redraw).
 * Non-image files are returned unchanged.
 * If compression fails, returns the original file as a graceful fallback.
 *
 * @param file - File to compress
 * @param onProgress - Optional 0–100 progress callback (runs in Web Worker)
 */
export async function compressImage(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<CompressedImageResult> {
  if (!file.type.startsWith('image/')) {
    return { file, originalSize: file.size, compressedSize: file.size, wasCompressed: false };
  }

  const originalSize = file.size;

  try {
    // Scale quality down as file gets larger; clamp between 0.4–0.92
    const estimatedQuality = Math.min(0.92, Math.max(0.4, TARGET_COMPRESSED_SIZE_BYTES / originalSize));

    const compressedFile = await imageCompression(file, {
      maxSizeMB: TARGET_COMPRESSED_SIZE_BYTES / (1024 * 1024), // 1.5
      maxWidthOrHeight: 2560,
      useWebWorker: true,
      initialQuality: estimatedQuality,
      // Convert PNG to JPEG for better compression ratio; EXIF stripped via canvas redraw
      fileType: file.type === 'image/png' ? 'image/jpeg' : undefined,
      onProgress,
    });

    const compressedSize = compressedFile.size;
    return {
      file: compressedFile,
      originalSize,
      compressedSize,
      wasCompressed: compressedSize < originalSize * 0.95,
    };
  } catch (error) {
    console.error('Image compression failed, uploading original:', error);
    // Graceful fallback: upload original rather than blocking the user
    return { file, originalSize, compressedSize: originalSize, wasCompressed: false };
  }
}

/** Format bytes as a human-readable size string. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Format a compression result as "1.2 MB (compressed from 4.8 MB)". */
export function formatCompressionResult(originalSize: number, compressedSize: number): string {
  return `${formatFileSize(compressedSize)} (compressed from ${formatFileSize(originalSize)})`;
}
